import API from "serviceshift-ui/api-client";

import { setMappedDialpadUser } from "@/composables/useDialpadContext";

export default {
  createMembershipProgram({ dispatch }, params) {
    return API.membershipProgram.create(params).then(() => {
      return dispatch("fetchMembershipPrograms");
    });
  },
  createWarrantyProgram({ dispatch }, params) {
    return API.warrantyProgram.create(params).then(() => {
      return dispatch("fetchWarrantyPrograms");
    });
  },
  fetchCustomizations({ commit }) {
    return API.makeRequest("/customizations.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "customizations",
        data: res.data
      });
    });
  },
  fetchTenantCustomizations({ commit }) {
    // Get tenant id from url
    const subdomain = window.location.hostname.split(".")[0];
    return API.tenant.getCustomizations(subdomain).then((res) => {
      commit("replace", {
        state: "tenantCustomizations",
        data: res.data
      });
    });
  },
  fetchTenant({ commit }) {
    // Get tenant id from url
    const subdomain = window.location.hostname.split(".")[0];
    commit("replace", {
      state: "fetchingTenant",
      data: true
    });
    return API.tenant
      .getBySubdomain(subdomain)
      .then((res) => {
        commit("replace", {
          state: "tenant",
          data: res.data
        });
        commit("replace", {
          state: "fetchingTenant",
          data: false
        });
      })
      .catch(() => {
        commit("replace", {
          state: "fetchingTenant",
          data: false
        });
      });
  },
  fetchPaymentMethods({ commit }) {
    return API.tenant.getPaymentMethods().then((res) => {
      commit("replace", {
        state: "paymentMethods",
        data: res.data
      });
    });
  },
  fetchPaymentMethodsSettings({ commit }) {
    return API.tenant.getPaymentMethodsSettings().then((res) => {
      commit("replace", {
        state: "paymentMethodsSettings",
        data: res.data
      });
    });
  },
  setTimezoneOptions({ commit }, data) {
    commit("replace", {
      state: "timezones",
      data
    });
  },
  updateMembershipProgram({ dispatch }, params) {
    return API.membershipProgram.update(params).then(() => {
      return dispatch("fetchMembershipPrograms");
    });
  },
  updateWarrantyProgram({ dispatch }, params) {
    return API.warrantyProgram.update(params).then(() => {
      return dispatch("fetchWarrantyPrograms");
    });
  },
  updatePaymentMethodsSettings({ commit }, data) {
    return API.tenant.updatePaymentMethodsSettings(data).then((res) => {
      commit("replace", {
        state: "paymentMethodsSettings",
        data: res.data
      });
    });
  },
  fetchIntegrations({ commit }) {
    return API.makeRequest("/v2/settings/integrations.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "integrations",
        data: res.data
      });
    });
  },
  fetchZapierIntegrations({ commit }) {
    return API.makeRequest("/v2/zapier_integrations.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "zapierIntegrations",
        data: res.data
      });
    });
  },
  fetchCampDigitalIntegrations({ commit }) {
    return API.makeRequest("/v2/integration/camp_digital.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "campDigitalConfig",
        data: res.data
      });
    });
  },
  updateIntegrations({ commit }, integrations) {
    return API.makeRequest("/v2/settings/integrations.json", {
      method: "POST",
      data: integrations
    }).then((res) => {
      commit("replace", {
        state: "integrations",
        data: res.data
      });
    });
  },
  updateZapierIntegrations({ commit }, zapierIntegrations) {
    return API.makeRequest("/v2/zapier_integrations/toggle.json", {
      method: "POST",
      data: zapierIntegrations
    }).then((res) => {
      commit("replace", {
        state: "zapierIntegrations",
        data: res.data
      });
    });
  },
  // Makes the tenant's Zapier 2.0 key (or replaces it). The response carries the
  // key once; the next fetch no longer has it.
  generateZapierApiKey({ commit }, params) {
    return API.makeRequest("/v2/zapier_integrations/api_keys.json", {
      method: "POST",
      data: params
    }).then((res) => {
      commit("replace", {
        state: "zapierIntegrations",
        data: res.data
      });
    });
  },
  // Adds the access the Zapier app has needed since a key was made to that key.
  // No key comes back: it does not change.
  addZapierApiKeyAccess({ commit }, { id, ...params }) {
    return API.makeRequest(`/v2/zapier_integrations/api_keys/${id}.json`, {
      method: "PATCH",
      data: params
    }).then((res) => {
      commit("replace", {
        state: "zapierIntegrations",
        data: res.data
      });
    });
  },
  // Cuts a Zapier key off at once.
  revokeZapierApiKey({ commit }, { id }) {
    return API.makeRequest(`/v2/zapier_integrations/api_keys/${id}.json`, {
      method: "DELETE"
    }).then((res) => {
      commit("replace", {
        state: "zapierIntegrations",
        data: res.data
      });
    });
  },
  updateCampDigitalIntegrations({ commit }, campDigitalConfig) {
    return API.makeRequest("/v2/integration/camp_digital/toggle.json", {
      method: "POST",
      data: campDigitalConfig
    }).then((res) => {
      commit("replace", {
        state: "campDigitalConfig",
        data: res.data
      });
    });
  },
  fetchBroccoliConfig({ commit }) {
    return API.makeRequest("/v2/integration/broccoli.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "broccoliConfig",
        data: res.data
      });
    });
  },
  enableBroccoli({ commit }) {
    return API.makeRequest("/v2/integration/broccoli/enable.json", {
      method: "POST"
    }).then((res) => {
      commit("replace", {
        state: "broccoliConfig",
        data: res.data
      });
    });
  },
  disableBroccoli({ commit }) {
    return API.makeRequest("/v2/integration/broccoli/disable.json", {
      method: "POST"
    }).then((res) => {
      commit("replace", {
        state: "broccoliConfig",
        data: res.data
      });
    });
  },
  provisionBroccoli() {
    return API.makeRequest("/v2/integration/broccoli/provision.json", {
      method: "POST"
    });
  },
  regenerateBroccoli() {
    return API.makeRequest("/v2/integration/broccoli/regenerate.json", {
      method: "POST"
    });
  },
  fetchCallRail({ commit }) {
    return API.makeRequest("/v2/call_rail.json", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "callRail",
        data: res.data
      });
    });
  },
  changeCallRail({ commit }, config) {
    return API.makeRequest("/v2/call_rail.json", {
      method: "POST",
      data: config
    }).then((res) => {
      commit("replace", {
        state: "callRail",
        data: res.data
      });
    });
  },
  dropCallRail({ commit }) {
    return API.makeRequest("/v2/call_rail.json", {
      method: "DELETE"
    }).then((res) => {
      commit("replace", {
        state: "callRail",
        data: res.data
      });
    });
  },
  fetchJBWarranties({ commit }) {
    return API.makeRequest("/v2/integration/jb_warranties", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "jbWarranties",
        data: res.data
      });
    });
  },
  changeJBWarranties({ commit }, config) {
    return API.makeRequest("/v2/integration/jb_warranties", {
      method: "POST",
      data: config
    }).then((res) => {
      commit("replace", {
        state: "jbWarranties",
        data: res.data
      });
    });
  },
  validateJBWarranties({ commit }, config) {
    return API.makeRequest("/v2/integration/jb_warranties/validate", {
      method: "POST",
      data: config
    }).then((res) => {
      return res.data;
    });
  },
  dropJBWarranties({ commit }) {
    return API.makeRequest("/v2/integration/jb_warranties", {
      method: "DELETE"
    }).then((res) => {
      commit("replace", {
        state: "jbWarranties",
        data: res.data
      });
    });
  },
  requestJB360Participation({ commit }, params) {
    return API.makeRequest(
      "/v2/integration/jb_warranties/request_participation",
      {
        method: "POST",
        data: params
      }
    ).then((res) => {
      commit("replace", {
        state: "jbWarranties",
        data: res.data
      });
    });
  },
  lookupJB360Dealer({ commit }) {
    return API.makeRequest("/v2/integration/jb_warranties/lookup_dealer", {
      method: "POST"
    }).then((res) => {
      commit("replace", {
        state: "jbWarranties",
        data: res.data
      });
    });
  },
  fetchStaxPayments({ commit }) {
    return API.makeRequest("/v2/integration/stax_payments", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "staxPayments",
        data: res.data
      });
    });
  },
  changeStaxPayments({ commit }, config) {
    return API.makeRequest("/v2/integration/stax_payments", {
      method: "POST",
      data: config
    }).then((res) => {
      commit("replace", {
        state: "staxPayments",
        data: res.data
      });
    });
  },
  dropStaxPayments({ commit }) {
    return API.makeRequest("/v2/integration/stax_payments", {
      method: "DELETE"
    }).then((res) => {
      commit("replace", {
        state: "staxPayments",
        data: res.data
      });
    });
  },
  // `failureWindow` picks how far back the outbound failures are grouped and counted.
  // Omitted on most calls, so the server applies its own default.
  fetchGhl({ commit }, { failureWindow } = {}) {
    return API.makeRequest("/v2/integration/ghl", {
      method: "GET",
      params: failureWindow ? { failure_window: failureWindow } : {}
    }).then((res) => {
      commit("replace", {
        state: "ghl",
        data: res.data
      });
    });
  },
  changeGhl({ commit }, config) {
    return API.makeRequest("/v2/integration/ghl", {
      method: "POST",
      data: config
    }).then((res) => {
      commit("replace", {
        state: "ghl",
        data: res.data
      });
    });
  },
  dropGhl({ commit }) {
    return API.makeRequest("/v2/integration/ghl", {
      method: "DELETE"
    }).then((res) => {
      commit("replace", {
        state: "ghl",
        data: res.data
      });
    });
  },
  // Starts the one-time sync again after it stopped. Saving already triggers one and
  // used to be the only way to ask, which reads as a reconnect and re-verifies a token
  // that was never the problem. The server decides whether a run is warranted.
  // The window is sent with the request rather than saved from the connect form: an
  // admin who declined the sweep on connect never chose one, and the choice belongs to
  // the moment they actually start it.
  retryGhlBackfill({ commit }, backfillWindow) {
    return API.makeRequest("/v2/integration/ghl/backfill", {
      method: "POST",
      data: backfillWindow ? { backfill_window: backfillWindow } : {}
    }).then((res) => {
      commit("replace", {
        state: "ghl",
        data: res.data
      });
    });
  },
  // Stops the sync where it is. The server closes the run rather than holding its jobs,
  // so resuming is the retry request above: each phase selects only records with no GHL
  // id yet, and a resumed run picks up exactly what is left.
  pauseGhlBackfill({ commit }) {
    return API.makeRequest("/v2/integration/ghl/pause_backfill", {
      method: "POST"
    }).then((res) => {
      commit("replace", {
        state: "ghl",
        data: res.data
      });
    });
  },
  // On-demand read of GHL's "allow more than one opportunity per contact"
  // setting. Kept out of fetchGhl so a Marketplace page load never blocks on a
  // GHL round-trip — the settings modal calls this only when opened / re-checked.
  checkGhlDuplicateOpportunities() {
    return API.makeRequest("/v2/integration/ghl/duplicate_check", {
      method: "GET"
    }).then((res) => res.data?.duplicate_opportunities_allowed ?? null);
  },
  // On-demand probe of the stored token's GHL scopes. Kept out of fetchGhl for the
  // same reason as the duplicate check: probing is 13 GHL round-trips and a
  // Marketplace page load must not wait on it. Callers fire this and fill in the
  // answer when it lands, so what is shown is always live rather than remembered.
  // `refresh` re-checks rather than reading the last stored answer. Sent when someone
  // lands on the page or opens the window, never from the poll that follows an
  // outstanding answer in — a probe per tick is thirteen GoHighLevel calls a second.
  checkGhlScopes(_ctx, { refresh = false } = {}) {
    return API.makeRequest("/v2/integration/ghl/scope_check", {
      method: "GET",
      params: refresh ? { refresh: true } : {}
    }).then((res) => res.data ?? null);
  },
  async fetchServiceFinanceConfig({ commit }) {
    const res = await API.makeRequest("/v2/integration/service_finance", {
      method: "GET"
    });
    commit("replace", {
      state: "serviceFinance",
      data: res.data
    });
  },
  async changeServiceFinanceConfig({ commit }, config) {
    const res = await API.makeRequest("/v2/integration/service_finance", {
      method: "POST",
      data: config
    });
    commit("replace", {
      state: "serviceFinance",
      data: res.data
    });
  },
  async dropServiceFinanceIntegration({ commit }) {
    const res = await API.makeRequest("/v2/integration/service_finance", {
      method: "DELETE"
    });
    commit("replace", {
      state: "serviceFinance",
      data: res.data
    });
  },
  fetchRingCentral({ commit }) {
    return API.makeRequest("/v2/ring_central", {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "ringCentral",
        data: res.data
      });
    });
  },
  changeRingCentral({ commit }, config) {
    return API.makeRequest("/v2/ring_central", {
      method: "POST",
      data: config
    }).then((res) => {
      commit("replace", {
        state: "ringCentral",
        data: res.data
      });
    });
  },
  async dropRingCentral({ commit }) {
    const res = await API.makeRequest("/v2/ring_central", {
      method: "DELETE"
    });
    commit("replace", {
      state: "ringCentral",
      data: res.data
    });
  },
  async fetchDialpad({ commit }) {
    const res = await API.makeRequest("/v2/integration/dialpad", {
      method: "GET"
    });
    commit("replace", { state: "dialpad", data: res.data });
  },
  async getDialpadAuthorizeUrl() {
    const res = await API.makeRequest("/v2/integration/dialpad/authorize_url", {
      method: "GET"
    });
    return res.data.url;
  },
  async disconnectDialpad({ commit, dispatch }) {
    const res = await API.makeRequest("/v2/integration/dialpad", {
      method: "DELETE"
    });
    commit("replace", { state: "dialpad", data: res.data });
    // Disconnecting removes every Dialpad user link, so a reconnect must not
    // offer calls under the old one.
    setMappedDialpadUser(null);
    // Drops the softphone, and the calls in it, without waiting for a reload.
    dispatch("fetchTenant");
  },
  async fetchDialpadUserMappings() {
    const res = await API.makeRequest("/v2/integration/dialpad/user_mappings", {
      method: "GET"
    });
    return res.data.admins;
  },
  async fetchDialpadUsers() {
    const res = await API.makeRequest(
      "/v2/integration/dialpad/user_mappings/dialpad_users",
      { method: "GET" }
    );
    return res.data.users;
  },
  async updateDialpadUserMapping(_context, { adminId, dialpadUserId }) {
    const res = await API.makeRequest(
      `/v2/integration/dialpad/user_mappings/${adminId}`,
      { method: "PATCH", data: { dialpad_user_id: dialpadUserId } }
    );
    return res.data;
  }
};
